import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useFormState, useWatch } from 'react-hook-form'
import { FormSectionTitle, PairingButton } from 'src/Libs/flow/components'
import { getDeviceDefaultName } from 'src/Libs/flow/parsers'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TFunction } from 'i18next'
import { HideDirtyStateProvider } from '../../providers'
import useModals from 'src/Hooks/useModals'
import { MyPvIo as PvIo } from 'src/Libs/flow/installation/forms'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { useDevicePairing } from '../../hooks'
import { deleteIndividualDevice } from 'src/Libs/flow/installation/api/save-installation/get-installation-delete-requests'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import useAccordionStyle from 'src/Libs/styles'
import { SettingsBox } from 'src/Elements/Data'
import { Select, TextField } from 'src/Libs/inputs/react-hook-form'
import { TextField as ShellTextField } from '@e3dc-react/shell/Elements/InputFields'
import { BoostConfig } from './BoostConfig'
import { checkTrueRecursively } from 'src/Libs/flow/utils'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

const optimizationModeOptions = (t: TFunction): Options<NonNullable<PvIo>['optimizationMode']> => [
  {
    value: 'All',
    label: t('installation.ioInterfaceEditor.myPv.optimizationMode.all'),
    description: t('installation.ioInterfaceEditor.myPv.chargingStrategy.allDescription'),
  },
  {
    value: 'Surplus',
    label: t('installation.ioInterfaceEditor.myPv.optimizationMode.surplus'),
    description: t('installation.ioInterfaceEditor.myPv.chargingStrategy.surplusDescription'),
  },
  {
    value: 'Solar',
    label: t('installation.ioInterfaceEditor.myPv.optimizationMode.solar'),
    description: t('installation.ioInterfaceEditor.myPv.chargingStrategy.solarDescription'),
  },
  {
    value: 'No',
    label: t('installation.ioInterfaceEditor.myPv.optimizationMode.no'),
    description: t('installation.ioInterfaceEditor.myPv.chargingStrategy.noDescription'),
  },
]

export const MyPvIo: React.FC = () => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()

  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const { control, getValues, setValue, reset } = useInstallationForm()
  const { dirtyFields, errors } = useFormState({ control, name: 'ioInterfaces.mypv' })

  const isDirty = checkTrueRecursively(dirtyFields.ioInterfaces?.mypv)
  const isError = !!Object.keys(errors.ioInterfaces?.mypv ?? {}).length

  const mypv = useWatch({
    control,
    name: 'ioInterfaces.mypv',
  })

  const { isNew, name, pairingId, deviceId, navigationId } = mypv ?? {}

  const { handlePairing } = useDevicePairing(mypv)

  const displayName = getDeviceDefaultName(name ?? '', 'MyPV I/O', deviceId ?? '', t)

  const { openConfirmationModal, openInstallationDeleteDialog } = useModals()
  const handleDelete = (): void => {
    openConfirmationModal({
      title: t('installation.deleteConfirmation.title'),
      text: `${t('installation.deleteConfirmation.selectedDevices')} ${displayName}`,
      buttonText: [t('common.cancel'), t('common.confirm')],
      onConfirm: async (onClose): Promise<void> => {
        const device = getValues('ioInterfaces.mypv')
        if (!device) {
          console.error('MyPV I/O was not found and could not be deleted.')
          return
        }

        Promise.resolve().then(() => {
          deleteIndividualDevice(device, getValues, setValue, openInstallationDeleteDialog, reset, t, authFetch)
        })
        onClose()
      },
    })
  }
  const { classes } = useAccordionStyle()

  const getLabel = (deviceId: string | undefined, pairingId: string | undefined): string => {
    if (!deviceId) {
      return t('common.pair')
    }
    return pairingId ? t('common.replace') : t('common.pair')
  }

  return (
    <HideDirtyStateProvider hideDirtyState={!!isNew}>
      <Box component={'section'} id={navigationId}>
        <AccordionItem
          title={<FormSectionTitle name={displayName} onClick={handleDelete} isDirty={isDirty} isError={isError} />}
          expanded
          className={classes.accordion}
          sx={{ margin: 0 }}
        >
          <Stack component={'section'} spacing={2} p={2}>
            <SettingsBox
              title="installation.submeterEditor.deviceName"
              suffix={
                <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                  <TextField
                    label={t('installation.submeterEditor.deviceName')}
                    name={'ioInterfaces.mypv.name'}
                    maxLength={100}
                    fullWidth
                    disabled={!canEdit}
                  />
                </Box>
              }
            />

            <Box component={'section'}>
              <Typography
                component="h3"
                sx={{
                  fontSize: 18,
                  fontWeight: 'bold',
                  color: theme => theme.palette.primary.dark,
                  mb: 2,
                  borderBottom: theme => `1px solid ${theme.palette.divider}`,
                }}
              >
                {t('installation.ioInterfaceEditor.controller')}
              </Typography>
              <SettingsBox
                title={'installation.productEditor.controllerDevice'}
                suffix={
                  <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                    <ShellTextField disabled label={t('installation.productEditor.controllerDevice')} value={t('installation.types.modbusTCP')} />
                  </Box>
                }
              />
              <SettingsBox
                title={'productEditor.pairingEditor.pairingID.mypv'}
                suffix={
                  <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                    <TextField fullWidth name={'ioInterfaces.mypv.pairingId'} label={t('productEditor.pairingEditor.pairingID.mypv')} disabled />
                  </Box>
                }
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <PairingButton
                  size="large"
                  sx={{ height: '100%', minWidth: { xs: '100%', sm: 'auto' } }}
                  onClick={handlePairing}
                  disabled={!canEdit}
                  icon="transfer"
                >
                  {getLabel(deviceId, pairingId)}
                </PairingButton>
              </Box>
            </Box>

            <Box component={'section'}>
              <Typography
                component="h3"
                sx={{
                  fontSize: 18,
                  fontWeight: 'bold',
                  color: theme => theme.palette.primary.dark,
                  mb: 2,
                  borderBottom: theme => `1px solid ${theme.palette.divider}`,
                }}
              >
                {t('installation.ioInterfaceEditor.mypv.optimizationMode.title')}
              </Typography>
              <SettingsBox
                title="installation.ioInterfaceEditor.mypv.optimizationMode.label"
                suffix={
                  <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                    <Select
                      options={optimizationModeOptions(t)}
                      name={'ioInterfaces.mypv.optimizationMode'}
                      label={t('installation.ioInterfaceEditor.mypv.optimizationMode.label')}
                      disabled={!canEdit}
                    />
                  </Box>
                }
              />

              <BoostConfig />
            </Box>
          </Stack>
        </AccordionItem>
      </Box>
    </HideDirtyStateProvider>
  )
}
